import React, {useState, useEffect} from 'react'
import {getSurveyResultGeneralData} from '../survey/list/core/_requests'
import {useLocation, useNavigate} from 'react-router-dom'
import SurveyResultsComponent from '../../modules/surveyanalytics/surveyanalytics'
import SurveyTableComponent from '../../modules/surveyanalytics/surveyanalyticstable'
import SurveyGaugeChart from '../../modules/surveyanalytics/SurveyGaugeChart'
import MatrixQuestionChartSwitcher from '../../modules/surveyanalytics/MatrixQuestionChartSwitcher'

// Yükleme göstergesi için bir CSS sınıfı ekleyin
const spinnerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  fontSize: '2rem',
  color: '#555',
}

const SurveyResultGeneral = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state: any = location.state

  const [surveyResults, setSurveyResults] = React.useState<any[]>([])
  const [survey, setSurvey] = React.useState<any>('')
  const [surveyName, setSurveyName] = React.useState<string>('')
  const [semesterName, setSemesterName] = React.useState<string>('')
  const [finished, setFinished] = React.useState(0)
  const [showMatrixCharts, setShowMatrixCharts] = useState(false)
  const [generatedAt] = useState<string>(() => new Date().toLocaleString('tr-TR'))
  const [surveyEndAt, setSurveyEndAt] = React.useState<string | null>(null)
  const [uniqueParticipantCount, setUniqueParticipantCount] = React.useState<number>(0)

  const formatSurveyDate = (value?: string | null) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return date.toLocaleDateString('tr-TR')
  }

  const isAnsweredSurveyValue = (value: any) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim().length > 0
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return `${value}`.trim().length > 0
  }

  const downloadCsvReport = () => {
    if (!Array.isArray(surveyResults) || surveyResults.length === 0) return
    const questionMap: Record<string, number> = {}
    surveyResults.forEach((entry: any) => {
      Object.entries(entry || {}).forEach(([key, value]) => {
        if (isAnsweredSurveyValue(value)) {
          questionMap[key] = (questionMap[key] || 0) + 1
        }
      })
    })

    const rows = Object.keys(questionMap)
      .sort((a, b) => a.localeCompare(b))
      .map((questionName) => {
        const answerCount = questionMap[questionName] || 0
        const ratio = ((answerCount / surveyResults.length) * 100).toFixed(2)
        return `"${questionName.replace(/"/g, '""')}",${answerCount},${ratio}`
      })

    const csvContent = ['Soru,Toplam Cevap,Cevaplanma Yüzdesi', ...rows].join('\n')

    const blob = new Blob([`\uFEFF${csvContent}`], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `anket-sonuc-ozet-${new Date().getTime()}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    if (state) {
      getSurveyResultGeneralData(state).then((surveyResponse: any) => {
        const surveyData = surveyResponse?.data
        const rawSurvey = surveyData?.surveyJson
        const rawSurveyName = surveyData?.surveyTypeName
        const rawSemesterName = surveyData?.semesterName
        const rawSurveyEndAt = surveyData?.surveyEndAt
        const rawUniqueParticipantCount = surveyData?.uniqueParticipantCount
        let parsedSurvey: any = ''

        try {
          parsedSurvey = rawSurvey ? JSON.parse(rawSurvey) : ''
        } catch (error) {
          parsedSurvey = ''
          console.error('Anket şablonu çözümlenemedi:', error)
        }

        const applicationArray = Array.isArray(surveyData?.surveyApplicationName)
          ? surveyData.surveyApplicationName
          : []

        const sanitizedResults = applicationArray
          .filter((entry: any) => typeof entry === 'string' && entry.length > 0)
          .map((entry: string) => {
            try {
              return JSON.parse(entry)
            } catch (error) {
              console.error('Anket sonucu çözümlenemedi:', error)
              return null
            }
          })
          .filter((entry: any) => {
            if (!entry || typeof entry !== 'object') {
              return false
            }

            return Object.values(entry).some((value: any) => {
              if (typeof value === 'string') {
                return value.trim().length > 0
              }

              if (Array.isArray(value)) {
                return value.length > 0
              }

              if (value && typeof value === 'object') {
                return Object.keys(value).length > 0
              }

              return false
            })
          })

        setSurvey(parsedSurvey)
        setSurveyName(rawSurveyName || parsedSurvey?.title || 'İsimsiz Anket')
        setSemesterName(rawSemesterName || 'Dönem bilgisi yok')
        setSurveyEndAt(rawSurveyEndAt || null)
        setUniqueParticipantCount(
          typeof rawUniqueParticipantCount === 'number' ? rawUniqueParticipantCount : sanitizedResults.length
        )
        setSurveyResults(sanitizedResults)
      }).catch((error) => {
        console.error('Anket sonuç verileri alınamadı:', error)
        setSurvey('')
        setSurveyResults([])
        setSurveyEndAt(null)
        setUniqueParticipantCount(0)
      })
      //console.log(state)
    }
  }, [state])

  useEffect(() => {
    //console.log(surveyResults)
    //console.log(survey)
    if (surveyResults.length > 0 && survey !== '') {
      setFinished(1)
    }
  }, [surveyResults, survey])

  return (
    <>
      {finished === 0 && (
        <div style={spinnerStyle}>
          <i className='fa fa-spinner fa-spin' aria-hidden='true'></i>
        </div>
      )}
      {finished === 1 && surveyResults && (
        <>
          <style>{`
                      @media print {
                        @page {
                          size: A4 landscape;
                          margin: 8mm;
                        }
                        .survey-result-actions {
                          display: none !important;
                        }
                        .survey-print-title {
                          display: block !important;
                        }
                        .card,
                        table,
                        #surveyAnalyticsContainer,
                        #surveyTableContainer,
                        #surveyAnalyticsContainer *,
                        #surveyTableContainer * {
                          break-inside: avoid;
                          page-break-inside: avoid;
                        }
                        .sa-question,
                        .sa-question__content,
                        .sa-visualizer,
                        .svc-creator__content-holder,
                        .tabulator-row,
                        .tabulator-tableholder {
                          break-inside: avoid !important;
                          page-break-inside: avoid !important;
                        }
                        .matrix-question-group,
                        .matrix-chart-card {
                          break-inside: avoid !important;
                          page-break-inside: avoid !important;
                        }
                        .matrix-chart-actions {
                          display: none !important;
                        }
                        .matrix-chart-canvas {
                          height: 220px !important;
                        }
                        .matrix-chart-canvas canvas {
                          max-height: 220px !important;
                        }
                      }
                    `}</style>
          <div className='card mb-5 border-0 bg-white survey-print-title'>
            <div className='card-body py-3 text-center'>
              <h4 className='mb-0'>{surveyName}</h4>
            </div>
          </div>
          <div className='card mb-5 border-0 bg-light'>
            <div className='card-body py-4'>
              <h5 className='mb-3'>Anket Sonuç Raporu - {surveyName}</h5>
              <ul className='mb-0'>
                <li>
                  <strong>Anket Tarihi:</strong> {formatSurveyDate(surveyEndAt)}
                </li>
                <li>
                  <strong>Rapor Tarihi:</strong> {generatedAt}
                </li>
                <li>
                  <strong>Dönem:</strong> {semesterName}
                </li>
                <li>
                  <strong>Tamamlanan Anket Sayısı:</strong> {surveyResults.length}
                </li>
                <li>
                  <strong>Tekil Katılımcı Sayısı:</strong> {uniqueParticipantCount}
                </li>
                <li>
                  <strong>Rapor İçeriği:</strong> Grafikler, kırılım analizleri ve tablo
                </li>
              </ul>
            </div>
          </div>
          <div className='d-flex justify-content-end gap-2 mb-5 survey-result-actions'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={() => setShowMatrixCharts((prev) => !prev)}
            >
              {showMatrixCharts
                ? 'SurveyJS Grafiklerini Göster'
                : 'Pasta / Bar Grafiklerini Göster'}
            </button>
            <button
              className='btn btn-sm btn-light-success'
              onClick={() => {
                const payload = {
                  generatedAt,
                  surveyEndAt,
                  uniqueParticipantCount,
                  responseCount: surveyResults.length,
                  surveyJson: survey,
                  surveyResults,
                }
                const blob = new Blob([JSON.stringify(payload, null, 2)], {
                  type: 'application/json;charset=utf-8',
                })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `anket-sonuc-raporu-${new Date().getTime()}.json`
                link.click()
                URL.revokeObjectURL(url)
              }}
            >
              JSON İndir
            </button>
            <button className='btn btn-sm btn-light-info' onClick={downloadCsvReport}>
              CSV Rapor İndir
            </button>
            <button className='btn btn-sm btn-primary' onClick={() => window.print()}>
              PDF / Yazdır (A4 Yatay)
            </button>
          </div>
          <SurveyGaugeChart surveyJson={survey} data={surveyResults} />
          {showMatrixCharts && (
            <MatrixQuestionChartSwitcher surveyJson={survey} surveyData={surveyResults} />
          )}
          <SurveyResultsComponent surveyJson={survey} surveyData={surveyResults} />
          <SurveyTableComponent surveyJson={survey} surveyData={surveyResults} />
        </>
      )}
    </>
  )
}

export default SurveyResultGeneral
